import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import { useLang } from '../i18n';

const css = `
  .jr { padding: 6px 16px 32px; }
  .jr-hero { padding: 18px 16px; border-radius: 14px; background: linear-gradient(135deg, rgba(246,195,68,.15), rgba(246,195,68,.04)); box-shadow: inset 0 0 0 1px rgba(246,195,68,.25); margin-bottom: 18px; text-align: center; }
  .jr-hero-icon { font-size: 32px; margin-bottom: 8px; }
  .jr-hero-title { font-size: 16px; font-weight: 800; color: #fff; }
  .jr-hero-sub { font-size: 12px; color: #f6c344; margin-top: 5px; line-height: 1.5; }
  .jr-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 18px; }
  .jr-card { padding: 14px 12px; border-radius: 12px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); display: flex; flex-direction: column; gap: 6px; }
  .jr-card-icon { font-size: 22px; }
  .jr-card-title { font-size: 12px; font-weight: 800; color: #fff; }
  .jr-card-text { font-size: 11px; color: #9b9ab0; line-height: 1.45; }
  .jr-section { margin-bottom: 16px; }
  .jr-section-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 10px; display: flex; align-items: center; gap: 7px; }
  .jr-section-title::before { content: ''; display: block; width: 3px; height: 14px; border-radius: 2px; background: linear-gradient(#f6c344, #ff9a3c); flex: none; }
  .jr-p { font-size: 12.5px; color: #9b9ab0; line-height: 1.65; }
  .jr-signs { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
  .jr-sign { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border-radius: 10px; background: rgba(255,93,108,.08); box-shadow: inset 0 0 0 1px rgba(255,93,108,.15); }
  .jr-sign-bullet { width: 6px; height: 6px; border-radius: 50%; background: #ff5d6c; flex: none; margin-top: 5px; }
  .jr-sign-text { font-size: 12px; color: #cfcee0; line-height: 1.4; }
  .jr-tools { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
  .jr-tool { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 11px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); cursor: pointer; }
  .jr-tool-ic { width: 36px; height: 36px; border-radius: 9px; display: grid; place-items: center; flex: none; font-size: 18px; }
  .jr-tool-ic.orange { background: rgba(246,195,68,.12); }
  .jr-tool-ic.red { background: rgba(255,93,108,.12); }
  .jr-tool-ic.blue { background: rgba(118,225,255,.12); }
  .jr-tool-info { flex: 1; min-width: 0; }
  .jr-tool-name { font-size: 13px; font-weight: 700; color: #fff; }
  .jr-tool-desc { font-size: 11px; color: #9b9ab0; margin-top: 2px; }
  .jr-tool-arrow { color: #9b9ab0; font-size: 16px; }
  .jr-hotline { margin-top: 18px; padding: 14px; border-radius: 12px; background: linear-gradient(90deg, rgba(129,135,246,.15), rgba(118,225,255,.06)); box-shadow: inset 0 0 0 1px rgba(118,225,255,.2); text-align: center; }
  .jr-hotline-label { font-size: 12px; color: #9b9ab0; }
  .jr-hotline-num { font-size: 22px; font-weight: 800; color: #76e1ff; letter-spacing: 1px; margin-top: 4px; }
  .jr-hotline-info { font-size: 11px; color: #757490; margin-top: 4px; }
  .jr-age { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 20px; padding: 12px; border-radius: 10px; background: rgba(255,93,108,.08); box-shadow: inset 0 0 0 1px rgba(255,93,108,.2); }
  .jr-age-badge { width: 36px; height: 36px; border-radius: 50%; background: #ff5d6c; display: grid; place-items: center; font-size: 11px; font-weight: 900; color: #fff; flex: none; }
  .jr-age-text { font-size: 12px; color: #cfcee0; line-height: 1.4; }
`;

const CONTENT = {
  fr: {
    backTitle: 'Jeu responsable',
    heroTitle: 'Jouez de façon responsable',
    heroSub1: 'Les jeux d\'argent doivent rester un loisir.',
    heroSub2: 'Ne jouez que ce que vous pouvez vous permettre de perdre.',
    cards: [
      { icon: '⏱️', title: 'Limites de dépôt', text: 'Fixez des plafonds journaliers, hebdomadaires ou mensuels.' },
      { icon: '🛑', title: 'Pause de jeu', text: 'Suspendez temporairement votre accès (24h à 6 mois).' },
      { icon: '🚫', title: 'Auto-exclusion', text: 'Fermez définitivement votre compte en cas de besoin.' },
      { icon: '📊', title: 'Suivi de jeu', text: 'Consultez votre historique de mises et de temps de jeu.' },
    ],
    signsTitle: 'Signes d\'une dépendance',
    signsIntro: 'Si vous reconnaissez l\'un de ces comportements, il est temps de chercher de l\'aide :',
    signs: [
      'Vous jouez avec de l\'argent dont vous avez besoin pour vivre',
      'Vous empruntez de l\'argent pour jouer',
      'Vous ne pouvez pas vous arrêter de jouer',
      'Vous négligez votre famille ou votre travail à cause du jeu',
      'Vous vous sentez agité ou irritable quand vous ne jouez pas',
      'Vous mentez sur votre comportement de jeu',
    ],
    toolsTitle: 'Outils de protection',
    tools: [
      { name: 'Limites de mise', desc: 'Définir des plafonds de dépôt' },
      { name: 'Auto-exclusion', desc: 'Suspendre ou fermer mon compte' },
      { name: 'Parler à quelqu\'un', desc: 'Contacter le support Roxs Sports' },
    ],
    hotlineLabel: 'Aide aux joueurs — Numéro vert',
    hotlineInfo: 'Appel gratuit — 7j/7 de 8h à 2h',
    ageText: 'Les jeux d\'argent sont strictement réservés aux personnes majeures. Jouez de manière responsable.',
  },
  es: {
    backTitle: 'Juego responsable',
    heroTitle: 'Juegue de forma responsable',
    heroSub1: 'Los juegos de azar deben seguir siendo un entretenimiento.',
    heroSub2: 'Juegue solo lo que pueda permitirse perder.',
    cards: [
      { icon: '⏱️', title: 'Límites de depósito', text: 'Fije topes diarios, semanales o mensuales.' },
      { icon: '🛑', title: 'Pausa de juego', text: 'Suspenda temporalmente su acceso (24h a 6 meses).' },
      { icon: '🚫', title: 'Autoexclusión', text: 'Cierre definitivamente su cuenta si lo necesita.' },
      { icon: '📊', title: 'Seguimiento de juego', text: 'Consulte su historial de apuestas y tiempo de juego.' },
    ],
    signsTitle: 'Señales de una adicción',
    signsIntro: 'Si reconoce alguno de estos comportamientos, es momento de buscar ayuda:',
    signs: [
      'Juega con dinero que necesita para vivir',
      'Pide dinero prestado para jugar',
      'No puede dejar de jugar',
      'Descuida a su familia o su trabajo por el juego',
      'Se siente inquieto o irritable cuando no juega',
      'Miente sobre su comportamiento de juego',
    ],
    toolsTitle: 'Herramientas de protección',
    tools: [
      { name: 'Límites de apuesta', desc: 'Definir topes de depósito' },
      { name: 'Autoexclusión', desc: 'Suspender o cerrar mi cuenta' },
      { name: 'Hablar con alguien', desc: 'Contactar con el soporte de Roxs Sports' },
    ],
    hotlineLabel: 'Ayuda al jugador — Línea gratuita',
    hotlineInfo: 'Llamada gratuita — 7 días/semana de 8h a 2h',
    ageText: 'Los juegos de azar están estrictamente reservados a personas mayores de edad. Juegue con responsabilidad.',
  },
  pt: {
    backTitle: 'Jogo responsável',
    heroTitle: 'Jogue de forma responsável',
    heroSub1: 'Os jogos de azar devem continuar sendo um entretenimento.',
    heroSub2: 'Jogue apenas o que você pode perder.',
    cards: [
      { icon: '⏱️', title: 'Limites de depósito', text: 'Defina limites diários, semanais ou mensais.' },
      { icon: '🛑', title: 'Pausa no jogo', text: 'Suspenda temporariamente seu acesso (24h a 6 meses).' },
      { icon: '🚫', title: 'Autoexclusão', text: 'Encerre definitivamente sua conta se necessário.' },
      { icon: '📊', title: 'Acompanhamento de jogo', text: 'Consulte seu histórico de apostas e tempo de jogo.' },
    ],
    signsTitle: 'Sinais de dependência',
    signsIntro: 'Se você reconhece algum destes comportamentos, é hora de buscar ajuda:',
    signs: [
      'Você joga com dinheiro do qual precisa para viver',
      'Você pega dinheiro emprestado para jogar',
      'Você não consegue parar de jogar',
      'Você negligencia sua família ou seu trabalho por causa do jogo',
      'Você se sente agitado ou irritado quando não joga',
      'Você mente sobre seu comportamento de jogo',
    ],
    toolsTitle: 'Ferramentas de proteção',
    tools: [
      { name: 'Limites de aposta', desc: 'Definir limites de depósito' },
      { name: 'Autoexclusão', desc: 'Suspender ou encerrar minha conta' },
      { name: 'Falar com alguém', desc: 'Contatar o suporte Roxs Sports' },
    ],
    hotlineLabel: 'Ajuda ao jogador — Linha gratuita',
    hotlineInfo: 'Ligação gratuita — 7 dias/semana das 8h às 2h',
    ageText: 'Os jogos de azar são estritamente reservados a maiores de idade. Jogue com responsabilidade.',
  },
  en: {
    backTitle: 'Responsible gaming',
    heroTitle: 'Play responsibly',
    heroSub1: 'Gambling should remain a leisure activity.',
    heroSub2: 'Only play what you can afford to lose.',
    cards: [
      { icon: '⏱️', title: 'Deposit limits', text: 'Set daily, weekly or monthly caps.' },
      { icon: '🛑', title: 'Take a break', text: 'Temporarily suspend your access (24h to 6 months).' },
      { icon: '🚫', title: 'Self-exclusion', text: 'Permanently close your account if needed.' },
      { icon: '📊', title: 'Play tracking', text: 'Review your history of stakes and play time.' },
    ],
    signsTitle: 'Signs of addiction',
    signsIntro: 'If you recognise any of these behaviours, it\'s time to seek help:',
    signs: [
      'You play with money you need to live on',
      'You borrow money to play',
      'You can\'t stop playing',
      'You neglect your family or work because of gambling',
      'You feel restless or irritable when you\'re not playing',
      'You lie about your gambling behaviour',
    ],
    toolsTitle: 'Protection tools',
    tools: [
      { name: 'Betting limits', desc: 'Set deposit caps' },
      { name: 'Self-exclusion', desc: 'Suspend or close my account' },
      { name: 'Talk to someone', desc: 'Contact Roxs Sports support' },
    ],
    hotlineLabel: 'Player help — Freephone',
    hotlineInfo: 'Free call — 7 days a week, 8am to 2am',
    ageText: 'Gambling is strictly reserved for adults. Play responsibly.',
  },
  it: {
    backTitle: 'Gioco responsabile',
    heroTitle: 'Gioca in modo responsabile',
    heroSub1: 'Il gioco d\'azzardo deve restare un divertimento.',
    heroSub2: 'Gioca solo ciò che puoi permetterti di perdere.',
    cards: [
      { icon: '⏱️', title: 'Limiti di deposito', text: 'Imposta limiti giornalieri, settimanali o mensili.' },
      { icon: '🛑', title: 'Pausa di gioco', text: 'Sospendi temporaneamente il tuo accesso (da 24h a 6 mesi).' },
      { icon: '🚫', title: 'Autoesclusione', text: 'Chiudi definitivamente il tuo account se necessario.' },
      { icon: '📊', title: 'Monitoraggio del gioco', text: 'Consulta la cronologia di puntate e tempo di gioco.' },
    ],
    signsTitle: 'Segnali di una dipendenza',
    signsIntro: 'Se riconosci uno di questi comportamenti, è il momento di chiedere aiuto:',
    signs: [
      'Giochi con denaro di cui hai bisogno per vivere',
      'Prendi in prestito denaro per giocare',
      'Non riesci a smettere di giocare',
      'Trascuri la famiglia o il lavoro a causa del gioco',
      'Ti senti agitato o irritabile quando non giochi',
      'Menti sul tuo comportamento di gioco',
    ],
    toolsTitle: 'Strumenti di protezione',
    tools: [
      { name: 'Limiti di puntata', desc: 'Impostare limiti di deposito' },
      { name: 'Autoesclusione', desc: 'Sospendere o chiudere il mio account' },
      { name: 'Parlare con qualcuno', desc: 'Contattare l\'assistenza Roxs Sports' },
    ],
    hotlineLabel: 'Aiuto al giocatore — Numero verde',
    hotlineInfo: 'Chiamata gratuita — 7 giorni su 7 dalle 8 alle 2',
    ageText: 'Il gioco d\'azzardo è riservato esclusivamente ai maggiorenni. Gioca in modo responsabile.',
  },
  nl: {
    backTitle: 'Verantwoord spelen',
    heroTitle: 'Speel verantwoord',
    heroSub1: 'Gokken moet een vrijetijdsbesteding blijven.',
    heroSub2: 'Speel alleen wat je je kunt veroorloven te verliezen.',
    cards: [
      { icon: '⏱️', title: 'Stortingslimieten', text: 'Stel dagelijkse, wekelijkse of maandelijkse limieten in.' },
      { icon: '🛑', title: 'Spelpauze', text: 'Schort je toegang tijdelijk op (24u tot 6 maanden).' },
      { icon: '🚫', title: 'Zelfuitsluiting', text: 'Sluit je account definitief indien nodig.' },
      { icon: '📊', title: 'Spelmonitoring', text: 'Bekijk je geschiedenis van inzetten en speeltijd.' },
    ],
    signsTitle: 'Tekenen van verslaving',
    signsIntro: 'Als je een van deze gedragingen herkent, is het tijd om hulp te zoeken:',
    signs: [
      'Je speelt met geld dat je nodig hebt om van te leven',
      'Je leent geld om te spelen',
      'Je kunt niet stoppen met spelen',
      'Je verwaarloost je gezin of je werk door het gokken',
      'Je voelt je onrustig of prikkelbaar wanneer je niet speelt',
      'Je liegt over je gokgedrag',
    ],
    toolsTitle: 'Beschermingstools',
    tools: [
      { name: 'Inzetlimieten', desc: 'Stortingslimieten instellen' },
      { name: 'Zelfuitsluiting', desc: 'Mijn account opschorten of sluiten' },
      { name: 'Met iemand praten', desc: 'Contact opnemen met Roxs Sports-support' },
    ],
    hotlineLabel: 'Spelershulp — Gratis nummer',
    hotlineInfo: 'Gratis gesprek — 7 dagen per week van 8u tot 2u',
    ageText: 'Gokken is strikt voorbehouden aan volwassenen. Speel verantwoord.',
  },
  tr: {
    backTitle: 'Sorumlu oyun',
    heroTitle: 'Sorumlu oynayın',
    heroSub1: 'Kumar bir eğlence olarak kalmalıdır.',
    heroSub2: 'Yalnızca kaybetmeyi göze alabileceğinizi oynayın.',
    cards: [
      { icon: '⏱️', title: 'Yatırım limitleri', text: 'Günlük, haftalık veya aylık limitler belirleyin.' },
      { icon: '🛑', title: 'Oyun molası', text: 'Erişiminizi geçici olarak askıya alın (24 saat - 6 ay).' },
      { icon: '🚫', title: 'Kendini dışlama', text: 'Gerekirse hesabınızı kalıcı olarak kapatın.' },
      { icon: '📊', title: 'Oyun takibi', text: 'Bahis ve oyun süresi geçmişinizi görüntüleyin.' },
    ],
    signsTitle: 'Bağımlılık belirtileri',
    signsIntro: 'Bu davranışlardan herhangi birini fark ederseniz, yardım aramanın zamanı gelmiştir:',
    signs: [
      'Geçiminiz için gereken parayla oynuyorsunuz',
      'Oynamak için borç para alıyorsunuz',
      'Oynamayı durduramıyorsunuz',
      'Kumar yüzünden ailenizi veya işinizi ihmal ediyorsunuz',
      'Oynamadığınızda huzursuz veya sinirli hissediyorsunuz',
      'Kumar davranışınız hakkında yalan söylüyorsunuz',
    ],
    toolsTitle: 'Koruma araçları',
    tools: [
      { name: 'Bahis limitleri', desc: 'Yatırım limitleri belirle' },
      { name: 'Kendini dışlama', desc: 'Hesabımı askıya al veya kapat' },
      { name: 'Biriyle konuş', desc: 'Roxs Sports desteğiyle iletişime geç' },
    ],
    hotlineLabel: 'Oyuncu yardımı — Ücretsiz hat',
    hotlineInfo: 'Ücretsiz arama — Haftanın 7 günü 08:00 - 02:00',
    ageText: 'Kumar yalnızca yetişkinlere mahsustur. Sorumlu oynayın.',
  },
  el: {
    backTitle: 'Υπεύθυνο παιχνίδι',
    heroTitle: 'Παίξτε υπεύθυνα',
    heroSub1: 'Τα τυχερά παιχνίδια πρέπει να παραμένουν ψυχαγωγία.',
    heroSub2: 'Παίζετε μόνο ό,τι μπορείτε να χάσετε.',
    cards: [
      { icon: '⏱️', title: 'Όρια κατάθεσης', text: 'Ορίστε ημερήσια, εβδομαδιαία ή μηνιαία όρια.' },
      { icon: '🛑', title: 'Παύση παιχνιδιού', text: 'Αναστείλετε προσωρινά την πρόσβασή σας (24 ώρες έως 6 μήνες).' },
      { icon: '🚫', title: 'Αυτοαποκλεισμός', text: 'Κλείστε οριστικά τον λογαριασμό σας εάν χρειαστεί.' },
      { icon: '📊', title: 'Παρακολούθηση παιχνιδιού', text: 'Δείτε το ιστορικό πονταρισμάτων και χρόνου παιχνιδιού.' },
    ],
    signsTitle: 'Σημάδια εθισμού',
    signsIntro: 'Αν αναγνωρίζετε κάποια από αυτές τις συμπεριφορές, είναι ώρα να ζητήσετε βοήθεια:',
    signs: [
      'Παίζετε με χρήματα που χρειάζεστε για να ζήσετε',
      'Δανείζεστε χρήματα για να παίξετε',
      'Δεν μπορείτε να σταματήσετε να παίζετε',
      'Παραμελείτε την οικογένεια ή την εργασία σας εξαιτίας του παιχνιδιού',
      'Νιώθετε ανήσυχοι ή ευερέθιστοι όταν δεν παίζετε',
      'Λέτε ψέματα για τη συμπεριφορά σας στο παιχνίδι',
    ],
    toolsTitle: 'Εργαλεία προστασίας',
    tools: [
      { name: 'Όρια στοιχήματος', desc: 'Ορισμός ορίων κατάθεσης' },
      { name: 'Αυτοαποκλεισμός', desc: 'Αναστολή ή κλείσιμο του λογαριασμού μου' },
      { name: 'Μιλήστε σε κάποιον', desc: 'Επικοινωνία με την υποστήριξη Roxs Sports' },
    ],
    hotlineLabel: 'Βοήθεια παίκτη — Γραμμή χωρίς χρέωση',
    hotlineInfo: 'Δωρεάν κλήση — 7 ημέρες την εβδομάδα από τις 8π.μ. έως τις 2π.μ.',
    ageText: 'Τα τυχερά παιχνίδια προορίζονται αυστηρά για ενήλικες. Παίξτε υπεύθυνα.',
  },
  ro: {
    backTitle: 'Joc responsabil',
    heroTitle: 'Jucați responsabil',
    heroSub1: 'Jocurile de noroc trebuie să rămână o distracție.',
    heroSub2: 'Jucați doar ceea ce vă permiteți să pierdeți.',
    cards: [
      { icon: '⏱️', title: 'Limite de depunere', text: 'Stabiliți limite zilnice, săptămânale sau lunare.' },
      { icon: '🛑', title: 'Pauză de joc', text: 'Suspendați temporar accesul (de la 24h la 6 luni).' },
      { icon: '🚫', title: 'Autoexcludere', text: 'Închideți definitiv contul dacă este necesar.' },
      { icon: '📊', title: 'Monitorizarea jocului', text: 'Consultați istoricul mizelor și al timpului de joc.' },
    ],
    signsTitle: 'Semne ale unei dependențe',
    signsIntro: 'Dacă recunoașteți unul dintre aceste comportamente, este momentul să căutați ajutor:',
    signs: [
      'Jucați cu bani de care aveți nevoie pentru a trăi',
      'Împrumutați bani pentru a juca',
      'Nu vă puteți opri din joc',
      'Vă neglijați familia sau munca din cauza jocului',
      'Vă simțiți agitat sau irascibil când nu jucați',
      'Mințiți despre comportamentul dvs. de joc',
    ],
    toolsTitle: 'Instrumente de protecție',
    tools: [
      { name: 'Limite de pariere', desc: 'Stabilirea limitelor de depunere' },
      { name: 'Autoexcludere', desc: 'Suspendarea sau închiderea contului meu' },
      { name: 'Vorbiți cu cineva', desc: 'Contactați asistența Roxs Sports' },
    ],
    hotlineLabel: 'Ajutor pentru jucători — Linie gratuită',
    hotlineInfo: 'Apel gratuit — 7 zile din 7, de la 8 la 2',
    ageText: 'Jocurile de noroc sunt strict rezervate persoanelor majore. Jucați responsabil.',
  },
};

export default function License() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  const [expanded, setExpanded] = useState(null);
  const toolMeta = [
    { ic: '⏱️', cls: 'orange', action: () => navigate('/deposit') },
    { ic: '🚫', cls: 'red', action: () => navigate('/profile/edit') },
    { ic: '💬', cls: 'blue', action: () => {} },
  ];

  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={c.backTitle} />
      <div className="jr">
        <div className="jr-hero">
          <div className="jr-hero-icon">🎯</div>
          <div className="jr-hero-title">{c.heroTitle}</div>
          <div className="jr-hero-sub">{c.heroSub1}<br />{c.heroSub2}</div>
        </div>

        <div className="jr-cards">
          {c.cards.map((card) => (
            <div className="jr-card" key={card.title}>
              <span className="jr-card-icon">{card.icon}</span>
              <span className="jr-card-title">{card.title}</span>
              <span className="jr-card-text">{card.text}</span>
            </div>
          ))}
        </div>

        <div className="jr-section">
          <div className="jr-section-title">{c.signsTitle}</div>
          <p className="jr-p">{c.signsIntro}</p>
          <div className="jr-signs">
            {c.signs.map((s, i) => (
              <div className="jr-sign" key={i}>
                <span className="jr-sign-bullet" />
                <span className="jr-sign-text">{s}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="jr-section">
          <div className="jr-section-title">{c.toolsTitle}</div>
          {c.tools.map((tool, i) => (
            <div className="jr-tool" key={tool.name} onClick={toolMeta[i].action}>
              <span className={'jr-tool-ic ' + toolMeta[i].cls}>{toolMeta[i].ic}</span>
              <div className="jr-tool-info">
                <div className="jr-tool-name">{tool.name}</div>
                <div className="jr-tool-desc">{tool.desc}</div>
              </div>
              <span className="jr-tool-arrow">›</span>
            </div>
          ))}
        </div>

        <div className="jr-hotline">
          <div className="jr-hotline-label">{c.hotlineLabel}</div>
          <div className="jr-hotline-num">0 974 75 13 13</div>
          <div className="jr-hotline-info">{c.hotlineInfo}</div>
        </div>

        <div className="jr-age">
          <span className="jr-age-badge">18+</span>
          <span className="jr-age-text">{c.ageText}</span>
        </div>
      </div>
    </PhoneShell>
  );
}
